import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { QueryClientContext } from '@tanstack/react-query';
import { useCampaignPageParam } from '../../useCampaignPageParam';
import {
  useCampaignPageSizeParam,
  PAGE_SIZE_OPTIONS,
} from '../../useCampaignPageSizeParam';
import { useCampaignPillParam } from '../../useCampaignPillParam';
import type { CampaignSearch } from '../../search';
import { toast } from 'sonner';
import { UploadSimple, UsersThree, UserCheck, UserCircleCheck, Prohibit } from '@phosphor-icons/react';
import { ChevronDown } from '../../../icons';
import { Info } from '../../../design-system/icons';
import {
  useCreatorsKPI,
  useRosterCounts,
  useApproveAllCreators,
  useCampaignUiStore,
  useCampaignCapabilitiesStore,
  useCampaignDraftsStore,
  useCampaignHeaderQuery,
  useLastImportResult,
  campaignKeys,
} from '@discovr/features';
import type { RosterRowRef } from '@discovr/features';
import type { CreatorDecision } from '@discovr/features';
import { EmptyState } from '../../../components/common/EmptyState';
import { formatCompact, formatMoneyExact, moneyFitClass } from '../../../lib/format';
import { AvgViewsCell } from './AvgViewsCell';
import { EngagementRateCell } from './EngagementRateCell';
import { EstimatedCPVCell } from './EstimatedCPVCell';
import { MetricsRecheckSlot } from './MetricsRecheckSlot';
import { rowMetrics, showMetricsRecheck } from './creatorMetricsStatus';
import { contentMetricsCopy, platformFetchesMetrics } from './contentMetricsCopy';
import { rowLookupStatus } from './creatorLookupStatus';
import { TruncatedText } from '../../../components/common/TruncatedText';
import { PlatformCreatorCell } from '../../../lib/platformProfile';
import { RosterTableShell } from '../parts/RosterTableShell';
import { RosterKpiCard } from '../parts/RosterKpiCard';
import { JumpToTopPill } from '../parts/JumpToTopPill';
import { SimpleTooltip } from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { isMain, isAddon } from '@discovr/types';
import { DataTable, ColumnsMenu, columnVisibilityView } from '../../../components/common/DataTable';
import type { ColumnDef } from '../../../components/common/DataTable';
import {
  unifiedApi,
  sumSelectedDeliverableCost,
  fmtDeliverableType,
  importBatchApi,
} from './helpers';
import {
  useRosterStatusTabs,
  useRosterTabSort,
  rosterSortStorageKey,
  useRosterColumnPreferences,
  useNow,
} from '../../hooks';
import type { RosterPage, RosterPillDef } from '../../hooks';
import { useRosterSearch } from '../../hooks/useRosterSearch';
import { usePublishSharePageInfo } from '../../hooks/usePublishSharePageInfo';
import { useCampaignPanelControls } from '../../useCampaignPanelControls';
import { usePublicView } from '../../publicView';
import { usePublicPillCapture, usePublicInsightsCapture } from '../../usePublicPillCapture';
import { ApprovalCard, type ConfirmAction } from './ApprovalCard';
import { AddScriptModal } from '../AddScriptModal';
import { UrlInputModal } from '../UrlInputModal';
import { liveUrlCopy } from '../LiveContentTab';
import { uploadCopy } from '../ContentTab/ContentTab';
import { deliverableContentKind } from '../ConfirmedTab.helpers';
import { quickActionStage, type QuickActionStage } from '../../utils/campaignPipeline';
import { rowDisplayName } from '../../utils/rowDisplayName';
import { CommercialPopover } from './CommercialPopover';
import { CreatorRowActions } from './CreatorRowActions';
import { useCreatorsRollback } from './CreatorsTab.rollback';
import { PreviousProductsPrompt } from '../PreviousProductsPrompt';
import { shouldOfferPreviousProducts } from '../previousProducts';
import { CreatorAnalysisButton } from '../parts/CreatorAnalysisButton';
import { resolveCreatorLookupBadge, rowLookup, type CreatorLookup } from './creatorLookupStatus';
import { CreatorLookupBadge } from './CreatorLookupBadge';
import { ProfileRecheckButton } from './ProfileRecheckButton';
import { profileRecheckRefetchInterval, useHasProfileRecheck } from './profileRecheckStore';
import type { CreatorsRow, CreatorsSubTab } from './types';
import { RosterScreenHeader } from '../parts/RosterScreenHeader';
import { CreatorsControls } from './CreatorsControls';
import { CreatorImportWizard } from './CreatorImportWizard';
import { ImportStatusModal } from './ImportStatusModal';
import { StatusBadge } from '../parts/StatusBadge';
import { adaptPublicRosterRowsAsCreatorsRows } from './publicRosterRowAdapter';
import { resolveSignalRow } from './resolveSignalRow';
import { useCreatorsBulkActions } from './useCreatorsBulkActions';
import { ApproveIconButton, ApprovePillButton, DeleteSelectedIconButton } from './RosterApproveControls';
import { approveAllControl, pickApproveControl, selectionApproveControl } from './approveControl';

/** No-op used for every pagination/pill/search callback in public read-only
 *  mode — belt-and-braces alongside `PublicCampaignShell`'s click-interceptor
 *  (which cancels the click before it ever reaches these handlers in the real
 *  wiring). Kept as a single stable reference so it never appears as a
 *  changed prop across renders. */
const PUBLIC_NOOP = () => {};

/** Whole-bucket fetch for the rejected-search path; the query's `select`
 *  slices the visible page. */
const MAX_FETCH_LIMIT = 10000;

/** Bounds the resumed-commercial-popover effect's wait for its row's
 *  trigger element to paint (see that effect's doc comment) — 20 attempts
 *  at 150ms apart is 3s total, comfortably past a normal render/virtualizer
 *  settle, short enough that giving up still reads as prompt to the user. */
const COMMERCIAL_TRIGGER_POLL_INTERVAL_MS = 150;
const COMMERCIAL_TRIGGER_POLL_MAX_ATTEMPTS = 20;

// "Approved" spans two BE statuses (comma-joined). "Rejected" hits a
// different endpoint (roster/rejected, not roster/creators) — buildPageQuery
// branches on pill.key rather than a status string. "All" = no status filter
// (BE's own pending+accepted union).
const CREATORS_PILLS_ADMIN: RosterPillDef<CreatorsSubTab>[] = [
  { key: 'pending', label: 'Pending', status: 'pending' },
  { key: 'approved', label: 'Approved', status: 'accepted_by_brand,accepted_by_admin' },
  { key: 'all', label: 'All' },
  { key: 'rejected', label: 'Rejected' },
];
const CREATORS_PILLS_BRAND: RosterPillDef<CreatorsSubTab>[] = [
  { key: 'pending', label: 'Pending', status: 'pending' },
  { key: 'approved', label: 'Approved', status: 'accepted_by_brand,accepted_by_admin' },
  { key: 'all', label: 'All' },
  { key: 'rejected', label: 'Rejected' },
];

export function CreatorsTab() {
  // ── Store reads (all at top — some useState initializers depend on them) ──
  const { campaignId } = useCampaignUiStore();
  const navigate = useNavigate();
  // Public (no-login) share view — see packages/ui/src/campaign-detail/
  // publicView.tsx. `PublicShareViewPage` never populates a real campaignId
  // for this surface (the public payload's campaign context carries no id at
  // all — see PublicCampaignContext), so every campaignId-gated query below
  // stays disabled on its own; `enabled: !isPublicView` on the three
  // aggregate hooks (KPI / counts / header) is explicit, documented
  // insurance on top of that, not the only thing standing between this tab
  // and a real network call.
  const {
    isPublicView,
    grant,
    rosterPage,
    campaignPlatform: publicCampaignPlatform,
    campCode: publicCampCode,
    tail: publicTail,
    requestLogin,
  } = usePublicView();
  const approvalRequest = useCampaignUiStore((s) => s.approvalRequest);
  const setApprovalRequest = useCampaignUiStore((s) => s.setApprovalRequest);
  const rejectRequest = useCampaignUiStore((s) => s.rejectRequest);
  const setRejectRequest = useCampaignUiStore((s) => s.setRejectRequest);
  const commercialPopoverRequest = useCampaignUiStore((s) => s.commercialPopoverRequest);
  const setCommercialPopoverRequest = useCampaignUiStore((s) => s.setCommercialPopoverRequest);
  const { openInsights, openConversation } = useCampaignPanelControls();

  const {
    permissions,
    onEditCreator,
    approveCreatorAsBrand,
    rejectCreatorApproval,
    deleteCreatorFromCampaign,
    confirmCreatorApproval,
    selectCreatorDeliverable,
    importResult,
    recheckCreatorProfile,
    recheckCreatorMetrics,
    reinstateCreator,
    moveCreatorToScriptReview,
    approveCreatorAndGoLive,
    approveCreatorAndAddContent,
    lane,
  } = useCampaignCapabilitiesStore();
  const campaignPipeline = useCampaignUiStore((s) => s.campaignPipeline);
  const quickStage = quickActionStage(campaignPipeline);
  // Admin sees the approved-first queue; brand sees pending-first. Derived
  // here (ahead of its other use below) so useNow can gate its interval on it
  // without disturbing hook call order.
  const isAdminViewer = !!permissions?.canApproveAcceptedCreators;
  const {
    openRollback: openRollbackToPending,
    blockedReason: rollbackToPendingBlockedReason,
    dialogs: rollbackDialogs,
  } = useCreatorsRollback(lane === 'admin' && !isPublicView && !!permissions?.canRollbackApproval);
  // Campaign platform drives PlatformCreatorCell's link target (YouTube
  // vs Instagram) and the Commercials column's main/add-on classification
  // (isMain/isAddon in packages/types/deliverableRegistry.ts). The authed
  // header query is disabled in public mode (no authed campaign id to fetch
  // with), so `campaignPlatform` there instead comes from the public
  // payload's own campaign context, threaded down via PublicViewValue —
  // see publicView.tsx.
  const { data: campaignHeader } = useCampaignHeaderQuery(campaignId ?? '', { enabled: !isPublicView });
  const campaignPlatform = isPublicView ? publicCampaignPlatform : campaignHeader?.platform;
  // Instagram and YouTube rows carry fetched view stats, so admins get their
  // status captions and the ⟳ re-fetch there — unless the BE says the fetch is
  // unavailable right now. A missing flag (older BE, public view) defers to
  // the platform alone.
  const fetchesMetrics =
    platformFetchesMetrics(campaignPlatform) && campaignHeader?.metrics_fetch_available !== false;
  const metricsCopy = contentMetricsCopy(campaignPlatform);

  // A coarse clock for metrics captions that depend on elapsed time without
  // any data change to trigger a re-render on their own. The only such
  // predicate is the one-hour stall boundary, and only an admin on a
  // metrics platform ever sees it — every other viewer takes the timestamp
  // once. Five minutes is ample resolution for an hour-long boundary, and
  // each tick rebuilds every column definition.
  const now = useNow(isAdminViewer && fetchesMetrics ? 300_000 : null);
  // The re-check store is keyed by campaign. campaignId is only unset in
  // public view, where recheckCreatorProfile is null and nothing is ever
  // marked, so the '' fallback can't leak a baseline across campaigns.
  const hasProfileRecheck = useHasProfileRecheck(campaignId ?? '');

  // The mutation function's identity changes on every pending/success flip,
  // so it is read through a ref: a click must re-render the clicked cell (its
  // per-row store subscription), not recompute every column definition.
  const recheckCreatorProfileRef = useRef(recheckCreatorProfile);
  useEffect(() => {
    recheckCreatorProfileRef.current = recheckCreatorProfile;
  }, [recheckCreatorProfile]);
  const hasProfileRecheckAction = !!recheckCreatorProfile;
  const recheckProfile = useCallback(
    (entryId: string) => {
      const recheck = recheckCreatorProfileRef.current;
      if (!recheck) return Promise.reject(new Error('profile re-check unavailable'));
      return recheck(entryId);
    },
    [],
  );

  // Same ref-indirection pattern as recheckCreatorProfile above, so a click's
  // pending/success flip re-renders only the clicked cell, not every column.
  const recheckCreatorMetricsRef = useRef(recheckCreatorMetrics);
  useEffect(() => {
    recheckCreatorMetricsRef.current = recheckCreatorMetrics;
  }, [recheckCreatorMetrics]);
  const hasMetricsRecheckAction = !!recheckCreatorMetrics;
  const recheckMetrics = useCallback(
    (entryId: string) => {
      const recheck = recheckCreatorMetricsRef.current;
      if (!recheck) return Promise.reject(new Error('metrics re-check unavailable'));
      return recheck(entryId);
    },
    [],
  );

  // Separate atomic selector — keeps re-renders from SSE ticks isolated to the
  // progress bar subtree; don't add transient high-frequency state to the full
  // destructure above.
  const importProgress = useCampaignCapabilitiesStore((s) => s.importProgress);

  // Only the admin app imports creators (canUploadCreatorSheet is false for every
  // brand role), and lastImportResult is only ever consumed behind that same flag.
  // Gate the fetch on it so the brand app never fires the guaranteed-403 request.
  const { data: lastImportResult } = useLastImportResult(campaignId ?? '', {
    enabled: !!permissions?.canUploadCreatorSheet,
  });

  // Zustand importResult (set by mutation onSuccess) takes precedence — it signals
  // "just uploaded in this session". Falls back to DB-persisted result so the
  // "View last import" button works after page reload.
  const effectiveResult = importResult ?? lastImportResult ?? null;

  const { setCreatorDecisions } = useCampaignDraftsStore();

  // ── Local state ────────────────────────────────────────────────────────────
  // Sub-filter pill is URL-owned. Empty-string fallback means "no URL pill set";
  // the effective sub-tab falls back to the role-based default (admin: approved,
  // brand: pending). When ?pill is present in the URL its value is used directly.
  const [urlPill, setCreatorsSubTab] = useCampaignPillParam('');
  // Public mode pins the active sub-tab to whichever pill the grant shared —
  // there is only ONE pill's real row data available, so the UI must not let
  // the visitor land on (or the URL default to) any other one.
  const creatorsSubTab: CreatorsSubTab =
    isPublicView && grant?.pill
      ? (grant.pill as CreatorsSubTab)
      : (urlPill as CreatorsSubTab) || (isAdminViewer ? 'approved' : 'pending');
  const [commercialPopover, setCommercialPopover] = React.useState<{
    creatorId: string;
    rect: DOMRect;
    triggerEl: HTMLElement;
  } | null>(null);
  const [optimisticDeliverableMap, setOptimisticDeliverableMap] = useState<
    Map<string, string[]>
  >(() => new Map());
  // Rows just approved/rejected/removed are hidden from the current sub-tab
  // IMMEDIATELY (client-level filter) so they vanish the instant the action
  // succeeds instead of waiting for the BE refetch. The mutation's invalidate()
  // refetch is the source of truth (BE-level filter) — once it lands the row is
  // gone server-side and the id is cleared from this set.
  const [optimisticallyRemovedIds, setOptimisticallyRemovedIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [importStatusOpen, setImportStatusOpen] = React.useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [approveAsBrandDeliverables, setApproveAsBrandDeliverables] = useState<string[]>([]);
  // Add Script (Quick Actions) — opens the same AddScriptModal the Scripts
  // tab uses. Deliverables are already chosen (the row's own selection state,
  // captured here at open-time) since the button is disabled until then, so
  // this modal only ever asks for the script itself.
  const [addScriptTarget, setAddScriptTarget] = useState<{
    creator: CreatorsRow;
    deliverables: string[];
    stage: QuickActionStage;
  } | null>(null);
  const [addScriptLoading, setAddScriptLoading] = useState(false);
  const commercialPopoverRef = React.useRef<HTMLDivElement>(null);
  // Set by the Escape-key handler below; consumed by the focus-restore
  // effect once the trigger row's post-close re-render has committed.
  const pendingFocusRestoreRef = React.useRef<string | null>(null);


  // ── React Query — status-scoped roster fetches ────────────────────────────
  const [page, setPage] = useCampaignPageParam();
  const [pageSize, setPageSize] = useCampaignPageSizeParam();
  // Active pill's total from /roster/counts (deduped by React Query with the
  // counts call below) — drives the hybrid search decision. Undefined until
  // counts load, so search falls back to the server until then.
  const { data: countsForSearch } = useRosterCounts(campaignId ?? '', { enabled: !isPublicView });
  const activeCreatorsTotal = !countsForSearch
    ? undefined
    : creatorsSubTab === 'pending'
      ? countsForSearch.pending
      : creatorsSubTab === 'approved'
        ? (countsForSearch.accepted_by_brand ?? 0) + (countsForSearch.accepted_by_admin ?? 0)
        : creatorsSubTab === 'rejected'
          ? countsForSearch.rejected_total
          : countsForSearch.creators_total;
  // Hybrid: instant client filter when the pill's whole set is on the page,
  // debounced ?search= when it spans pages. Ends the per-keystroke request storm.
  // Stable identity so the search-reset effect dep does not churn every render.
  const resetPageForSearch = useCallback(() => setPage(1), [setPage]);
  const searchState = useRosterSearch(activeCreatorsTotal, pageSize, {
    onQueryChange: resetPageForSearch,
  });

  // Clear optimistic removals when the sub-tab changes — a stale hide from a
  // previous visit to this pill shouldn't hide a row on the next one.
  // (useRosterStatusTabs itself resets the page on pill change.)
  const prevCreatorsSubTabRef = useRef(creatorsSubTab);
  useEffect(() => {
    if (prevCreatorsSubTabRef.current === creatorsSubTab) return; // mount or unchanged (StrictMode-safe)
    prevCreatorsSubTabRef.current = creatorsSubTab;
    setOptimisticallyRemovedIds(new Set());
  }, [creatorsSubTab]);

  const getRowSearchText = useCallback((c: CreatorsRow) => {
    return [
      (c.platform as { name?: string } | undefined)?.name ?? '',
      (c as { creator?: { name?: string } }).creator?.name ?? '',
      c.platform?.username ?? '',
    ].join(' ');
  }, []);

  // Per-tab column sort (persisted), threaded into the roster query below.
  const rosterSort = useRosterTabSort<CreatorsSubTab>(rosterSortStorageKey('creators', campaignId), creatorsSubTab);

  // SERVER-SIDE pagination: only the ACTIVE pill fetches, and it fetches exactly
  // one page. Pill badges come from /roster/counts, so the inactive pills issue
  // no request at all. "Rejected" routes to a different endpoint
  // (roster/rejected), so branch on pill.key rather than status.
  //
  // `enabled: !isPublicView` is a real, independently-honored gate:
  // `useRosterStatusTabs` ANDs this closure's own `enabled` with its
  // `!!campaignId` check rather than overwriting it, and campaignId is never
  // populated in public mode, so both conditions disable this query.
  const buildPageQuery = useCallback(
    (pill: RosterPillDef<CreatorsSubTab>, pageNum: number, pageSize: number) => {
      const cid = campaignId ?? '';
      const isRejected = pill.key === 'rejected';
      // Rejected has no server-side field sort (BE ListRejected is recency-only);
      // every other pill carries its own persisted {by,dir} into the query.
      const sortSpec = isRejected ? undefined : rosterSort.specFor(pill.key);

      // Debounced server term (undefined in client mode / when empty). Client-mode
      // filtering is applied at display time via searchState.filterRows.
      const serverSearch = searchState.serverSearch;

      // Rejected has no server-side ?search=, so a LARGE rejected bucket being
      // searched (serverSearch set) falls back to the whole-bucket fetch + client
      // filter. Small buckets take the normal page path and filter on display.
      // Everything else (creators pills) sends ?search= to the BE.
      if (serverSearch && isRejected) {
        return {
          // Key by search — not page/size. One whole-bucket fetch per search
          // term; `select` slices the visible page from the cached bucket.
          queryKey: campaignKeys.rosterTab(cid, 'rejected', {
            phase: 'bucket',
            search: serverSearch,
            limit: MAX_FETCH_LIMIT,
          }),
          queryFn: async () => {
            const res = await unifiedApi.listRejected(cid, { page: 1, limit: MAX_FETCH_LIMIT });
            const q = serverSearch.toLowerCase();
            const rows = (res.entries as CreatorsRow[]).filter((c) =>
              getRowSearchText(c).toLowerCase().includes(q),
            );
            return { entries: rows, total: rows.length, totalPages: 1 };
          },
          select: (bucket: RosterPage<CreatorsRow>): RosterPage<CreatorsRow> => {
            const start = (pageNum - 1) * pageSize;
            return {
              entries: bucket.entries.slice(start, start + pageSize),
              total: bucket.total,
              totalPages: Math.max(1, Math.ceil(bucket.total / pageSize)),
            };
          },
          staleTime: 30_000,
          enabled: !isPublicView,
        };
      }

      return {
        queryKey: campaignKeys.rosterTab(cid, isRejected ? 'rejected' : 'creators', {
          status: pill.status,
          page: pageNum,
          limit: pageSize,
          search: serverSearch,
          // Part of the cache key — a distinct query, re-fetched sorted.
          sort_by: sortSpec?.by,
          sort_dir: sortSpec?.dir,
        }),
        enabled: !isPublicView,
        queryFn: async () => {
          const res = isRejected
            ? await unifiedApi.listRejected(cid, { page: pageNum, limit: pageSize })
            : await unifiedApi.listCreators(cid, {
                status: pill.status,
                page: pageNum,
                limit: pageSize,
                search: serverSearch,
                sort_by: sortSpec?.by,
                sort_dir: sortSpec?.dir,
              });
          return {
            entries: res.entries as CreatorsRow[],
            total: res.total,
            totalPages: res.totalPages,
          };
        },
        staleTime: 30_000,
        // Polls only while an admin profile re-check on this page is settling.
        refetchInterval: profileRecheckRefetchInterval(cid, hasProfileRecheck),
      };
    },
    [campaignId, searchState.serverSearch, getRowSearchText, isPublicView, rosterSort, hasProfileRecheck],
  );


  const creatorsPillDefs = isAdminViewer ? CREATORS_PILLS_ADMIN : CREATORS_PILLS_BRAND;

  // Every pill badge from ONE aggregation — no pill fetches rows to show a number.
  const { data: rosterCounts } = useRosterCounts(campaignId ?? '', { enabled: !isPublicView });
  const creatorsCounts = useMemo<Partial<Record<CreatorsSubTab, number>>>(
    () => ({
      pending: rosterCounts?.pending ?? 0,
      // The Approved pill spans brand- AND admin-accepted (both still sit on
      // this tab until the creator accepts).
      approved: (rosterCounts?.accepted_by_brand ?? 0) + (rosterCounts?.accepted_by_admin ?? 0),
      all: rosterCounts?.creators_total ?? 0,
      rejected: rosterCounts?.rejected_total ?? 0,
    }),
    [rosterCounts],
  );

  const {
    pills: creatorsPills,
    pageRows: rawPageRows,
    activeRows,
    total,
    pageCount,
    isLoading: isInitialLoading,
    isError: isCreatorsQueryError,
    isRefetching: isLoadingEntries,
    isPlaceholderData: isPagePlaceholder,
  } = useRosterStatusTabs<CreatorsRow, CreatorsSubTab>({
    campaignId: campaignId || undefined,
    activeTab: creatorsSubTab,
    pills: creatorsPillDefs,
    buildPageQuery,
    counts: creatorsCounts,
    pageSize,
    page,
    setPage,
    clientFilter: searchState.clientQuery
      ? (r) => getRowSearchText(r).toLowerCase().includes(searchState.clientQuery)
      : undefined,
  });

  // Publishes pageSize/pageCount for the admin-header page-kind share button
  // (which has no table state of its own) — see usePublishSharePageInfo.
  // Disabled in public mode: that share button doesn't exist in the public
  // shell, and pageCount here is the disabled roster query's own (always
  // wrong: stuck at 1) value, not the real grant snapshot — see the hook's
  // own doc comment.
  usePublishSharePageInfo(pageSize, pageCount, !isPublicView);

  const allCount = creatorsPills.find((p) => p.key === 'all')?.count ?? 0;

  // Public mode: the granted pill's real rows, adapted from the flat
  // PublicRosterRow shape into what these columns read. `rawPageRows` above
  // stays permanently empty (its query is disabled) — this is the ACTUAL
  // data source rendered in public mode, sourced from the already-fetched
  // public payload rather than any query.
  const publicRows = useMemo(
    () => adaptPublicRosterRowsAsCreatorsRows(rosterPage?.rows ?? []),
    [rosterPage],
  );

  // Pill badges: every pill EXCEPT the granted one shows a skeleton (never a
  // false "0" — a public link never carries every pill's count, only the
  // one it granted).
  //
  // The granted pill shows its real row count ONLY when the grant is a
  // single page (total_pages <= 1) — there, the one already-fetched page's
  // row count IS the pill's whole total, exactly. For a MULTI-page grant the
  // granted pill's true total is genuinely unknown (only one page's worth of
  // rows was ever shared), so showing `rows.length` there would understate
  // the real queue the same way a fake `0` would — it goes skeleton too,
  // consistent with the pagination footer's "Showing X–Y of Z" text below
  // (same `total_pages > 1` condition drives both).
  const displayPills = useMemo(() => {
    if (!isPublicView) return creatorsPills;
    const grantedPillKey = grant?.pill;
    const grantedTotalPages = rosterPage?.total_pages ?? grant?.totalPages ?? 1;
    const grantedRowCount = rosterPage?.rows.length ?? 0;
    return creatorsPills.map((p) => ({
      ...p,
      count:
        p.key === grantedPillKey
          ? grantedTotalPages > 1
            ? ('loading' as const)
            : grantedRowCount
          : ('loading' as const),
    }));
  }, [creatorsPills, isPublicView, grant?.pill, grant?.totalPages, rosterPage]);

  // Tab-aggregated KPI — BE-computed across the full Creators tab, not
  // derived from one page's rows. Disabled in public mode: a public link
  // never carries campaign-wide aggregates.
  const kpiQuery = useCreatorsKPI(campaignId ?? '', { enabled: !isPublicView });
  const approveAllMutation = useApproveAllCreators(campaignId ?? '');

  // Bulk "approve all eligible" — shown contextually: brand on the Pending
  // sub-tab (approves pending creators that have a main deliverable selected),
  // admin on the Approved/brand-accepted sub-tab (advances them to admin
  // approval). The BE enforces eligibility per role; the gate below just hides
  // the action when the relevant queue is empty.
  const showApproveAll =
    (!isAdminViewer && creatorsSubTab === 'pending') ||
    (isAdminViewer && creatorsSubTab === 'approved');
  const handleApproveAll = async () => {
    // Optimistically hide the rows that will be approved so they vanish from the
    // current sub-tab the instant you click — mirrors the single-row path. The
    // eligibility check matches the BE (brand: rows with a main deliverable
    // selected; admin: every brand-accepted row), so the refetch reconciles
    // without flash-back. Restore on error / nothing-approved. activeRows is
    // the current server page, so only rows on this page hide; other pages
    // are reconciled by the refetch.
    const eligibleIds = activeRows
      .filter((e) =>
        isAdminViewer
          ? false // admin approval keeps rows on the Approved sub-tab; nothing leaves to hide
          : (e.deliverables ?? []).some(
              (d) => isMain(d.type, campaignPlatform ?? '') && d.is_selected,
            ),
      )
      .map((e) => String(e.id));
    const restore = () =>
      setOptimisticallyRemovedIds((prev) => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        eligibleIds.forEach((id) => next.delete(id));
        return next;
      });
    if (eligibleIds.length > 0) {
      setOptimisticallyRemovedIds((prev) => {
        const next = new Set(prev);
        eligibleIds.forEach((id) => next.add(id));
        return next;
      });
    }
    try {
      const res = await approveAllMutation.mutateAsync();
      const summary = [
        `Approved ${res.approved_count} creator${res.approved_count === 1 ? '' : 's'}`,
      ];
      if (res.skipped_count > 0) summary.push(`${res.skipped_count} skipped`);
      if (res.failed_count > 0) summary.push(`${res.failed_count} failed`);
      if (res.approved_count === 0 && res.failed_count === 0) {
        restore(); // nothing moved — bring the rows back
        toast.info(
          res.skipped_count > 0
            ? `No creators approved · ${res.skipped_count} had no main deliverable selected`
            : 'No eligible creators to approve',
        );
      } else if (res.failed_count > 0) {
        // Partial failure — un-hide everything; the refetch re-removes the ones
        // that actually moved and keeps the failed rows visible (we can't tell
        // which specific ids failed from the count-only result).
        restore();
        toast.warning(summary.join(' · '));
      } else {
        toast.success(summary.join(' · '));
      }
    } catch {
      restore();
      toast.error('Failed to approve creators');
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reconciles optimistic deliverable overrides against freshly-fetched page rows as they load; functional updater returns prev unchanged when nothing to drop, so no cascading render.
    setOptimisticDeliverableMap((prev) => {
      if (prev.size === 0) return prev;

      let changed = false;
      const next = new Map(prev);
      for (const entry of rawPageRows) {
        if (!next.has(entry.id)) continue;
        next.delete(entry.id);
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [rawPageRows]);

  // Once a refetched page no longer contains an optimistically-removed id, the
  // BE has caught up (the row left this sub-tab's status filter) — drop the id
  // so the set never leaks stale entries. Skip on an empty page: [] is not a
  // confirmation the row left (it's also the loading/blank state), and clearing
  // then would briefly un-hide rows. A stale id left in the set is harmless —
  // it only filters a row that's no longer present.
  useEffect(() => {
    if (rawPageRows.length === 0) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reconciles optimistically-removed ids against freshly-fetched page rows as they load; functional updater returns prev unchanged when nothing to drop, so no cascading render.
    setOptimisticallyRemovedIds((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set(rawPageRows.map((e) => String(e.id)));
      let changed = false;
      const next = new Set(prev);
      for (const id of prev) {
        if (!present.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [rawPageRows]);

  // Drives the inline "Approve all (N)" pill. Server-computed, so it reflects
  // the WHOLE queue rather than the visible page. Admin advances brand-accepted
  // rows specifically (not rows already accepted_by_admin); brand advances
  // pending rows that already have a main deliverable selected (the BE's
  // approvable_by_brand predicate).
  const approveAllEligibleCount = isAdminViewer
    ? (rosterCounts?.accepted_by_brand ?? 0)
    : (rosterCounts?.approvable_by_brand ?? 0);

  // Approval signal from the insights drawer (Brand Safety tab's Approve button).
  // Opens the same ApprovalCard dialog the row buttons use, with the same
  // pre-selected deliverables + role-aware variant, then clears the signal.
  useEffect(() => {
    if (!approvalRequest) return;
    const reqId = String(approvalRequest.id);
    // The roster query may still be loading (a resumed approve_creator/reject
    // action can set this signal BEFORE CreatorsTab has even mounted, unlike
    // the insights-drawer caller which always sets it from an already-loaded
    // tab) — wait for activeRows to settle rather than falling back to an
    // incomplete {id}-only stub with no real status. Once settled and still
    // not found, give up and tell the user instead of leaving the signal
    // armed forever (see resolveSignalRow's doc comment).
    const outcome = resolveSignalRow(activeRows, reqId, !isInitialLoading);
    if (outcome.kind === 'wait') return;
    if (outcome.kind === 'absent') {
      toast.error("We couldn't find this creator on this page — try navigating there directly.");
      setApprovalRequest(null);
      return;
    }
    const row = outcome.row;
    const status = (row as { status?: string }).status;
    const types = (row.deliverables ?? []).filter((d) => d.is_selected).map((d) => d.type);
    if (isAdminViewer && status === 'pending' && permissions?.canApproveOnBehalfOfBrand) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- responds to a one-shot approval signal from the insights drawer, opening the dialog then clearing the signal (setApprovalRequest(null)); an event handler, not a prop→state sync.
      setApproveAsBrandDeliverables(types);
      setConfirmAction({ variant: 'approve-as-brand', creator: row });
    } else if (status === 'pending') {
      setApproveAsBrandDeliverables(types);
      setConfirmAction({ variant: 'brand-approve', creator: row });
    } else if (isAdminViewer && status === 'accepted_by_brand') {
      setApproveAsBrandDeliverables(types);
      setConfirmAction({ variant: 'admin-approve', creator: row });
    }
    setApprovalRequest(null);
  }, [approvalRequest, activeRows, isInitialLoading, isAdminViewer, permissions, setApproveAsBrandDeliverables, setApprovalRequest]);

  // Reject signal from the insights drawer header's Reject button — mirror of the
  // approval effect above. Opens the same reject dialog the row buttons use, then
  // clears the signal.
  useEffect(() => {
    if (!rejectRequest) return;
    const reqId = String(rejectRequest.id);
    // Same rationale as the approvalRequest effect above — wait for
    // activeRows to settle instead of falling back to an incomplete stub,
    // but give up and notify once settled and still not found (see
    // resolveSignalRow's doc comment).
    const outcome = resolveSignalRow(activeRows, reqId, !isInitialLoading);
    if (outcome.kind === 'wait') return;
    if (outcome.kind === 'absent') {
      toast.error("We couldn't find this creator on this page — try navigating there directly.");
      setRejectRequest(null);
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- responds to a one-shot reject signal, opening the dialog then clearing the signal (setRejectRequest(null)); an event handler, not a prop→state sync.
    setConfirmAction({ variant: 'reject', creator: outcome.row });
    setRejectRequest(null);
  }, [rejectRequest, activeRows, isInitialLoading, setRejectRequest]);

  // Client-level optimistic filter applied on top of the BE-ordered page —
  // hides rows that were just approved/rejected/removed until the refetch
  // lands. Search is already applied upstream (useRosterStatusTabs
  // clientFilter), so this only needs the optimistic-removal pass.
  // Public mode swaps in the real granted-pill rows (publicRows) in place of
  // the disabled query's own (permanently empty) rawPageRows — see
  // `buildPageQuery`'s doc comment above. The optimistic-
  // removal filter is a no-op there regardless: no mutation ever fires in
  // public mode (every write callback is null and the shell's
  // click-interceptor cancels the click before any handler runs), so
  // optimisticallyRemovedIds stays empty.
  const effectivePageRows = isPublicView ? publicRows : rawPageRows;
  const visibleCreators = React.useMemo<CreatorsRow[]>(
    () =>
      optimisticallyRemovedIds.size === 0
        ? effectivePageRows
        : effectivePageRows.filter((c) => !optimisticallyRemovedIds.has(String(c.id))),
    [effectivePageRows, optimisticallyRemovedIds],
  );

  const hideRowsFromCurrentTab = useCallback((ids: string[]) => {
    setOptimisticallyRemovedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  // Everything that decides which rows the page query returns, so the bulk
  // selection can tell "refetched without this row" from "a different page".
  const bulkPageKey = [
    creatorsSubTab,
    page,
    pageSize,
    JSON.stringify(rosterSort.specFor(creatorsSubTab) ?? null),
    searchState.serverSearch ?? '',
    searchState.clientQuery,
  ].join('|');

  const bulkActions = useCreatorsBulkActions({
    campaignId: campaignId ?? '',
    subTab: creatorsSubTab,
    isAdminViewer,
    isPublicView,
    permissions,
    pageRows: effectivePageRows,
    pageKey: bulkPageKey,
    pageIsPlaceholder: !isPublicView && isPagePlaceholder,
    hiddenRowIds: optimisticallyRemovedIds,
    platform: campaignPlatform ?? '',
    optimisticSelections: optimisticDeliverableMap,
    onRowsLeftTab: hideRowsFromCurrentTab,
  });

  // Resumed save_deliverables signal — mirrors the approvalRequest/
  // rejectRequest effects above, except it also drives a positioned popover
  // (needs the trigger's live DOM rect, only available once this row has
  // painted on the real, authed page; see commercialPopoverRequest's doc
  // comment on useCampaignUiStore). Restoring into optimisticDeliverableMap
  // is what lets commercialPopoverRows (above) seed the popover pre-checked.
  //
  // Looks up the row in `activeRows` first, falling back to `visibleCreators`
  // (the public-view case, where `activeRows`' query is disabled and empty).
  // Both are page-scoped, so a row on a different page is indistinguishable
  // from one that doesn't exist; resolveSignalRow resolves that to 'absent'
  // and the caller notifies the user rather than leaving the signal armed.
  //
  // The trigger-paint wait is a bounded poll (COMMERCIAL_TRIGGER_POLL_*): the
  // effect's deps may never change again once the row lookup settles, so
  // relying on a re-render could leave the signal armed indefinitely. Timing
  // out clears it with the same toast UX as the row-not-found branch.
  //
  // Declared after `visibleCreators` to avoid a temporal-dead-zone reference
  // in this effect's dependency array.
  useEffect(() => {
    if (!commercialPopoverRequest) return;
    const reqId = commercialPopoverRequest.entryId;
    const row =
      activeRows.find((e) => String(e.id) === reqId) ?? visibleCreators.find((e) => String(e.id) === reqId);
    if (!row) {
      if (!isInitialLoading) {
        toast.error("We couldn't find this creator on this page — try navigating there directly.");
        setCommercialPopoverRequest(null);
      }
      return;
    }

    let attempts = 0;
    const tryOpen = (): boolean => {
      const trigger = document.querySelector(
        `[data-commercial-trigger][data-creator-id="${CSS.escape(reqId)}"]`,
      );
      if (!trigger) return false;
      const rect = trigger.getBoundingClientRect();
      const restoredTypes = [
        ...commercialPopoverRequest.selectedMainIds,
        ...commercialPopoverRequest.selectedAddonIds,
      ];
      setOptimisticDeliverableMap((prev) => {
        const next = new Map(prev);
        next.set(commercialPopoverRequest.entryId, restoredTypes);
        return next;
      });
      setCommercialPopover({
        creatorId: commercialPopoverRequest.entryId,
        rect,
        triggerEl: trigger as HTMLElement,
      });
      setCommercialPopoverRequest(null);
      return true;
    };

    if (tryOpen()) return;

    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (tryOpen() || attempts >= COMMERCIAL_TRIGGER_POLL_MAX_ATTEMPTS) {
        window.clearInterval(intervalId);
        if (attempts >= COMMERCIAL_TRIGGER_POLL_MAX_ATTEMPTS) {
          toast.error("We couldn't find this creator on this page — try navigating there directly.");
          setCommercialPopoverRequest(null);
        }
      }
    }, COMMERCIAL_TRIGGER_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [commercialPopoverRequest, activeRows, visibleCreators, isInitialLoading, setCommercialPopoverRequest]);

  // Creators tab KPIs are scoped to the candidate funnel (pending +
  // accepted_by_brand + accepted_by_admin — the BE's ViewCandidate),
  // restricted to rows with any deliverable selected; finalized rows have
  // their own KPI row on the Finalized tab.
  //
  // BE-aggregated — no FE recomputation, no dependency on the current page or
  // sub-tab, so numbers stay stable across sub-tab switches and pagination.
  const kpi = kpiQuery.data;

  React.useEffect(() => {
    if (!commercialPopover) return;
    const handleClickOutside = (e: MouseEvent) => {
      // If the click is on a trigger button, let the button's onClick handle
      // the toggle — don't close here or the button always re-opens.
      if ((e.target as Element).closest('[data-commercial-trigger]')) return;
      if (
        commercialPopoverRef.current &&
        !commercialPopoverRef.current.contains(e.target as Node)
      ) {
        setCommercialPopover(null);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Keyboard-only dismissal has no click to leave focus behind, so
      // restore it to the trigger row on close (mouse-driven outside-click
      // close above doesn't need this — the click itself already moved
      // focus). Deferred to the focus-restore effect below rather than done
      // here: closing recomputes `columns` (it depends on commercialPopover),
      // which remounts the row cells, so the trigger button focused
      // synchronously in this handler would immediately be replaced.
      pendingFocusRestoreRef.current = commercialPopover.creatorId;
      setCommercialPopover(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [commercialPopover]);

  // Runs after the popover-close render (and its `columns` recompute, which
  // remounts the row cells — see the Escape handler above) has committed, so
  // the trigger button being queried here is the current DOM node, not one
  // about to be replaced.
  React.useEffect(() => {
    if (commercialPopover || !pendingFocusRestoreRef.current) return;
    const creatorId = pendingFocusRestoreRef.current;
    pendingFocusRestoreRef.current = null;
    const trigger = document.querySelector<HTMLElement>(
      `[data-commercial-trigger][data-creator-id="${CSS.escape(creatorId)}"]`,
    );
    trigger?.focus();
  }, [commercialPopover]);

  // The page-level import popup (ImportProgressPopup on CampaignDetailPage)
  // owns the live progress/result surface across every tab, so this tab never
  // auto-opens the blocking ImportStatusModal — it opens only from the explicit
  // "View last import" button. Publish the wizard-open state so the popup
  // suppresses itself while the uploader is in the wizard (which already shows
  // the same progress inline).
  const setImportWizardOpen = useCampaignCapabilitiesStore((s) => s.setImportWizardOpen);
  const setImportResult = useCampaignCapabilitiesStore((s) => s.setImportResult);
  React.useEffect(() => {
    setImportWizardOpen(uploadModalOpen);
    return () => setImportWizardOpen(false);
  }, [uploadModalOpen, setImportWizardOpen]);

  // Closing the wizard clears only the completed RESULT (the uploader already
  // saw it inline, so the page-level popup must not re-pop it). A still-running
  // import keeps its progress, so the popup takes over live instead of blinking.
  const closeUploadWizard = React.useCallback(() => {
    setUploadModalOpen(false);
    setImportResult(null);
  }, [setImportResult]);

  // Imports land as pending, but an admin opens on Approved: after a finished
  // import that added creators the list moves to where they are. Optional
  // context read: the roster refresh is skipped where no query client exists.
  const queryClient = React.useContext(QueryClientContext);
  const showImportedCreators = React.useCallback(
    (added: number) => {
      if (added > 0) setCreatorsSubTab('pending');
    },
    [setCreatorsSubTab],
  );
  const refreshRoster = React.useCallback(() => {
    if (!campaignId) return;
    void queryClient?.invalidateQueries({ queryKey: campaignKeys.rosterAll(campaignId) });
  }, [queryClient, campaignId]);

  // ── Column definitions for DataTable ────────────────────────────────────────
  // Inline deliverable-cell content: DeliverableCell.tsx renders a <TableCell>
  // wrapper that can't nest inside DataTable's own <td>, so its inner JSX is
  // replicated here.
  // min-w-0 is required for the inner `truncate` span to actually clip — a
  // flex item's default min-width is `auto` (won't shrink below content), so
  // without it long joined deliverable labels would overflow instead.
  const BOX =
    'flex w-full min-w-0 items-center justify-between gap-1 rounded-lg border border-border bg-card px-3 py-2 text-left text-[12px] font-medium';

  const renderDeliverableCellContent = (
    status: string,
    labels: string[],
    creatorId: string,
  ) => {
    const isOpen = commercialPopover?.creatorId === creatorId;
    const hasLabels = labels.length > 0;
    // A creator picked for bulk approval with no commercial yet, or whose
    // profile can't be offered: the red box is the reminder, since bulk
    // approval is refused until that is fixed (or the row is deselected).
    const needsCommercial =
      bulkActions.needsCommercialIds.has(creatorId) || bulkActions.unofferableIds.has(creatorId);
    const display = hasLabels ? labels.join(', ') : 'Select';

    // On a pending row the picker OPENS in public mode too (matches the authed
    // empty-state affordance instead of rendering a permanent skeleton), but
    // it can never SELECT or SAVE.
    // `data-public-allowed` on the trigger lets the shell's click-interceptor
    // through for the open/close click itself; CommercialPopover is safe to
    // expose because `accessLevel: 'read'` (set for every public session,
    // see PublicShareViewPage.tsx) makes its own `isReadOnly` gate disable
    // every option button and hide Save — independent of, and unaffected by,
    // interception. `selectCreatorDeliverable` is also null in the public
    // capabilities object as a second, independent guard against a write.
    if (status !== 'pending') {
      return hasLabels ? (
        <div className={`${BOX} text-muted-foreground`}>
          <TruncatedText text={display} />
        </div>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    }

    const onClick = (e: React.MouseEvent<HTMLElement>) => {
      const el = e.currentTarget;
      const rect = el.getBoundingClientRect();
      setCommercialPopover((prev) =>
        prev?.creatorId === creatorId ? null : { creatorId, rect, triggerEl: el },
      );
    };

    return (
      <button
        type="button"
        onClick={onClick}
        data-commercial-trigger="true"
        data-creator-id={creatorId}
        data-public-allowed=""
        data-needs-commercial={needsCommercial || undefined}
        aria-invalid={needsCommercial || undefined}
        className={cn(
          BOX,
          'transition-colors text-muted-foreground',
          needsCommercial ? 'border-destructive bg-destructive/5 hover:border-destructive' : 'hover:border-foreground/30',
        )}
      >
        <TruncatedText text={display} />
        <ChevronDown
          size={14}
          className={`shrink-0 transition-transform text-muted-foreground ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
    );
  };

   
  // "View analysis" in public mode: the trigger carries `data-public-allowed`
  // (see the `creator` column's cell below) so the shell's click-interceptor
  // lets it through, and its click branches here — prompt sign-in with the
  // click captured as a resumable `open_insights` pending action. Keyless
  // (shadow) creators have no username to resume against, so
  // usePublicInsightsCapture falls back to a bare sign-in prompt for those.
  //
  // Declared before `columns` (which closes over it) and above the
  // `!permissions` guard further down (rules of hooks).
  const handlePublicViewInsights = usePublicInsightsCapture('creators');

  // Approve/Reject on the roster row, in public mode — never opens
  // ApprovalCard (an anonymous visitor must not type a reject reason, or any
  // other text input, before signing in). Captures the resumable action
  // directly from the icon button's own click instead. Hoisted above
  // `columns` for the same reason as handlePublicViewInsights above.
  const handlePublicRosterAction = useCallback(
    (type: 'approve_creator' | 'reject', row: CreatorsRow) => {
      const entryUsername = row.platform?.username?.trim();
      if (entryUsername && publicCampCode && publicTail) {
        requestLogin({
          type,
          campCode: publicCampCode,
          tail: publicTail,
          tab: 'creators',
          entryUsername,
          createdAt: Date.now(),
        });
      } else {
        requestLogin();
      }
    },
    [publicCampCode, publicTail, requestLogin],
  );

  const columns = useMemo((): ColumnDef<CreatorsRow>[] => {
    const plat = campaignPlatform ?? '';

    // Shared renderer for the three action columns. `actions` is the everyday
    // Approve · Chat · Reject cluster (brand + admin); `quick` is the
    // admin-only Approve as Brand · Add Script write-access fast path;
    // `manage` is the remaining row-management cluster — Edit · Remove ·
    // Reinstate. Same props, the component picks which buttons to show per
    // column.
    const renderRowActions = (c: CreatorsRow, column: 'actions' | 'quick' | 'manage') => {
      // The whole tab early-returns null when permissions is absent (see below),
      // so cells never render without it; guard here to satisfy the type instead
      // of asserting non-null.
      if (!permissions) return null;
      const hasOptimisticDeliverable = optimisticDeliverableMap.has(c.id);
      const optimisticTypes = optimisticDeliverableMap.get(c.id) ?? [];
      // Public mode: the row's own Approve/Reject buttons are gated on
      // canApproveRejectCreators, which is deliberately `false` on the
      // read-level permissions object PublicShareViewPage.tsx seeds
      // (correct for a real authed read-only brand_member, who genuinely
      // cannot approve/reject). An anonymous public visitor is a different
      // case — flip ONLY the rendering gate so the buttons show (every write
      // control a signed-in brand user would see); the
      // click still never mutates (see onConfirmReject/onConfirmBrandApprove
      // below) — same pattern CommercialPopover's own isPublicView override
      // already uses for its Save-disable gate.
      const rowActionPermissions = isPublicView
        ? { ...permissions, canApproveRejectCreators: true }
        : permissions;
      return (
        <CreatorRowActions
          column={column}
          c={c}
          isAdminViewer={isAdminViewer}
          creatorsSubTab={creatorsSubTab}
          permissions={rowActionPermissions}
          setConvCreator={openConversation}
          optimisticSelectedTypes={
            hasOptimisticDeliverable ? optimisticTypes : undefined
          }
          onEditCreator={onEditCreator}
          onConfirmReject={(creator) => {
            if (isPublicView) {
              handlePublicRosterAction('reject', creator);
              return;
            }
            setConfirmAction({ variant: 'reject', creator });
          }}
          onConfirmDelete={(creator) =>
            setConfirmAction({ variant: 'delete', creator })
          }
          onConfirmApproveAsBrand={(creator, preSelectedTypes) => {
            setApproveAsBrandDeliverables(preSelectedTypes);
            setConfirmAction({ variant: 'approve-as-brand', creator });
          }}
          onConfirmReinstate={(creator) => setConfirmAction({ variant: 'reinstate', creator })}
          quickStage={quickStage}
          onConfirmMoveToScriptReview={(creator, preSelectedTypes) => {
            setAddScriptTarget({ creator, deliverables: preSelectedTypes, stage: quickStage });
          }}
          onConfirmBrandApprove={(creator, preSelectedTypes) => {
            if (isPublicView) {
              handlePublicRosterAction('approve_creator', creator);
              return;
            }
            setApproveAsBrandDeliverables(preSelectedTypes);
            setConfirmAction({ variant: 'brand-approve', creator });
          }}
          onConfirmAdminApprove={(creator, preSelectedTypes) => {
            setApproveAsBrandDeliverables(preSelectedTypes);
            setConfirmAction({ variant: 'admin-approve', creator });
          }}
          onRollbackToPending={openRollbackToPending}
          rollbackToPendingBlockedReason={rollbackToPendingBlockedReason}
        />
      );
    };

    return [
      {
        id: 'creator',
        alwaysVisible: true,
        sortable: !isPublicView,
        header: 'Creators',
        // Matches every other roster tab's Creator column (Confirmed,
        // Scripts) exactly, so the tables read at a consistent width.
        minWidth: 320,
        maxWidth: 340,
        grow: true,
        sticky: 'left',
        cell: (c) => {
          // The BE stamps lookup_status on the row for both platforms, and
          // the profile re-check endpoint now supports both too — it reads
          // the campaign's own platform and re-queues that platform's job,
          // so a YouTube row re-fetches via YouTube and an Instagram row via
          // Instagram (see AdminRecheckCreatorProfile). Brands see the
          // verdict too (a creator is never hidden for it); the re-check
          // stays an admin action. Public share viewers never get it.
          const lookup: CreatorLookup = rowLookup(c);
          const lookupBadge = resolveCreatorLookupBadge(isPublicView ? undefined : lookup.status, {
            isAdmin: lane === 'admin',
          });
          const recheckNode =
            lookupBadge.showRecheck && hasProfileRecheckAction ? (
              <ProfileRecheckButton
                campaignId={campaignId ?? ''}
                entryId={c.id}
                handle={c.platform?.username}
                lookup={lookup}
                recheck={recheckProfile}
              />
            ) : null;
          return (
          // Identity opens the analysis drawer; a dedicated ChartBar chip on the
          // right edge is the always-visible affordance (the standalone
          // "Analysis" column was retired). Two sibling buttons in a plain div —
          // never nested — so both are valid, focusable targets. The handle is
          // plain text here; its profile link lives in the drawer header.
          <div className="-mx-2 -my-1.5 flex w-full items-center gap-2 rounded-xl px-2 py-1.5 transition-colors hover:bg-muted/60">
            <button
              type="button"
              onClick={() =>
                isPublicView ? handlePublicViewInsights(c.platform?.username?.trim()) : openInsights(c as unknown as RosterRowRef)
              }
              data-public-allowed=""
              className="flex min-w-0 flex-1 items-center rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              title="View analysis"
            >
              <PlatformCreatorCell
                creator={c as unknown as Record<string, unknown>}
                campaignPlatform={campaignPlatform}
                linkHandle={false}
                subtitle={<CreatorLookupBadge pill={lookupBadge.pill} />}
              />
            </button>
            {recheckNode}
            <CreatorAnalysisButton
              onOpen={() =>
                isPublicView ? handlePublicViewInsights(c.platform?.username?.trim()) : openInsights(c as unknown as RosterRowRef)
              }
              creatorName={
                (c as { creator?: { name?: string } }).creator?.name ?? c.platform?.username
              }
            />
          </div>
          );
        },
      },
      {
        id: 'status',
        alwaysVisible: true,
        header: 'Status',
        // Fixed (not grow) — sized to the widest label across every status
        // this pill can show anywhere in the roster ("Changes Requested"),
        // so the column never stretches to fill leftover table width.
        minWidth: 160,
        maxWidth: 160,
        cell: (c) =>
          shouldOfferPreviousProducts(c) ? (
            <div className="flex flex-col items-start gap-1.5">
              <StatusBadge status={c.status ?? 'unknown'} />
              <PreviousProductsPrompt row={c} />
            </div>
          ) : (
            <StatusBadge status={c.status ?? 'unknown'} />
          ),
      },
      {
        id: 'followers',
        sortable: !isPublicView,
        header: 'Followers',
        // Fixed — formatCompact tops out around "999.99M"/"B", which fits
        // comfortably; no reason for this to stretch.
        minWidth: 90,
        maxWidth: 90,
        align: 'right',        cell: (c) => (
          <span
            className="font-medium text-foreground"
            title={c.snapshot_metrics.followers > 0 ? c.snapshot_metrics.followers.toLocaleString() : undefined}
          >
            {c.snapshot_metrics.followers > 0
              ? formatCompact(c.snapshot_metrics.followers)
              : c.snapshot_metrics.followers_hidden
                ? 'Hidden'
                : '—'}
          </span>
        ),
      },
      {
        id: 'avg_views',
        label: 'Avg Views',
        sortable: !isPublicView,
        header: (
          <span className="inline-flex items-center justify-end gap-1">
            Avg. Views
            <SimpleTooltip
              content={
                isAdminViewer ? metricsCopy.avgViewsInfoAdmin : metricsCopy.avgViewsInfo
              }
            >
              <span className="inline-flex cursor-default text-muted-foreground">
                <Info size={13} />
              </span>
            </SimpleTooltip>
          </span>
        ),
        // Fixed — wider than the other stat columns: the header carries an
        // info-tooltip icon, and the admin value can carry a status caption
        // plus the ⟳ re-fetch button beside it.
        minWidth: 128,
        maxWidth: 128,
        align: 'right',
        cell: (c) => {
          const lookup = fetchesMetrics ? rowLookupStatus(c) : undefined;
          // Always the slot, never a conditional button: the slot owns the
          // "still settling" half of the gate, which needs a store hook.
          const recheckNode =
            isAdminViewer && hasMetricsRecheckAction && fetchesMetrics ? (
              <MetricsRecheckSlot
                campaignId={campaignId ?? ''}
                entryId={c.id}
                handle={c.platform?.username}
                metrics={rowMetrics(c.snapshot_metrics)}
                showRecheck={showMetricsRecheck(c.snapshot_metrics, lookup, now)}
                recheck={recheckMetrics}
              />
            ) : null;
          return (
            <AvgViewsCell
              m={c.snapshot_metrics}
              detailed={isAdminViewer}
              lookup={lookup}
              now={now}
              copy={metricsCopy}
              recheck={recheckNode}
              lastError={'metrics_last_error' in c ? c.metrics_last_error : undefined}
              campaignId={campaignId}
              entryId={c.id}
            />
          );
        },
      },
      {
        id: 'engagement_rate',
        sortable: !isPublicView,
        header: 'Eng. Rate',
        // Fixed — "XX.XX%" never needs more than this.
        minWidth: 90,
        maxWidth: 90,
        align: 'right',
        cell: (c) => (
          <EngagementRateCell
            m={c.snapshot_metrics}
            detailed={isAdminViewer}
            lookup={fetchesMetrics ? rowLookupStatus(c) : undefined}
            now={now}
            copy={metricsCopy}
            campaignId={campaignId}
            entryId={c.id}
          />
        ),
      },
      {
        id: 'commercials',
        alwaysVisible: true,
        header: 'Commercials',
        // Single column for the whole selection: main first, then add-ons
        // (per product — no separate Add-ons column; the popover is where the
        // two categories are distinguished). The joined-labels cell already
        // truncates (same pattern as Creator), so the cap only costs a hover
        // tooltip.
        minWidth: 160,
        maxWidth: 220,
        cell: (c) => {
          const creatorId = c.id;
          const creatorStatus = c.status;
          const allSelected = c.deliverables.filter((d) => d.is_selected);
          const persistedMainType =
            allSelected.find((d) => isMain(d.type, plat))?.type ?? null;
          const hasOptimisticDeliverable = optimisticDeliverableMap.has(c.id);
          const optimisticTypes = optimisticDeliverableMap.get(c.id) ?? [];
          const pendingTypesAll = hasOptimisticDeliverable
            ? optimisticTypes
            : [
                ...(persistedMainType ? [persistedMainType] : []),
                ...allSelected.filter((d) => isAddon(d.type, plat)).map((d) => d.type),
              ];
          // Main first, then add-ons — one stable reading order.
          const orderLabels = (types: string[]) =>
            [
              ...types.filter((t) => isMain(t, plat)),
              ...types.filter((t) => isAddon(t, plat)),
            ].map(fmtDeliverableType);
          const labels =
            creatorStatus !== 'pending'
              ? orderLabels(allSelected.map((d) => d.type))
              : orderLabels(pendingTypesAll);
          return renderDeliverableCellContent(creatorStatus, labels, creatorId);
        },
      },
      {
        id: 'total_payable',
        header: 'Total Payable',
        // Fixed — sized for the exact 2-decimal amount (moneyFitClass shrinks longer ones).
        minWidth: 132,
        maxWidth: 132,
        align: 'right',
        cell: (c) => {
          // No isPublicView skeleton here:
          // Total Payable is this ROW's own figure, not a campaign-wide
          // aggregate — a public grant already scopes the visitor to exactly
          // this page/page-size of rows, so showing one row's own committed
          // cost carries the same scope as its status/followers/etc. The
          // `estimated_budget` used below is real on an adapted public row
          // now — see publicRosterRowAdapter.ts.
          const displayCost =
            'estimated_budget' in c
              ? c.deliverables.some((d) => d.is_selected) ? c.estimated_budget : 0
              : sumSelectedDeliverableCost(c.deliverables);
          const cost = displayCost > 0 ? formatMoneyExact(displayCost) : '—';
          return (
            <span className={`font-medium text-muted-foreground tabular-nums ${moneyFitClass(cost)}`}>
              {cost}
            </span>
          );
        },
      },
      {
        id: 'est_cpv',
        label: 'Est. CPV',
        sortable: !isPublicView,
        header: (
          <span className="inline-flex items-center gap-1">
            Est. CPV
            <SimpleTooltip content="Estimated cost per view of the primary deliverable (its cost ÷ its estimated views)">
              <span className="inline-flex cursor-default text-muted-foreground">
                <Info size={13} />
              </span>
            </SimpleTooltip>
          </span>
        ),
        // Fixed — "₹XX.XX" never needs more than this.
        minWidth: 90,
        maxWidth: 90,
        align: 'right',
        cell: (c) => (
          <EstimatedCPVCell
            cpv={'estimated_cpv' in c ? c.estimated_cpv : null}
            metrics={c.snapshot_metrics}
            now={now}
            campaignId={campaignId}
            entryId={c.id}
          />
        ),
      },
      {
        id: 'actions',
        alwaysVisible: true,
        header: 'Actions',
        // Fixed — sized for the Approve · Chat · Reject cluster; rows with
        // fewer buttons (e.g. chat-only) still hug centred via the flex layout.
        minWidth: 132,
        maxWidth: 132,
        align: 'center',
        cell: (c) => renderRowActions(c, 'actions'),
      },
      // Quick Actions — Approve as Brand · Add Script, sits between Actions
      // and Manage. Visible to any admin-side user with write+ campaign
      // access — split into its own column because neither is an
      // elevated/destructive action the way Edit/Remove are. Both buttons
      // only apply to pending creators (each moves them OUT of pending), so
      // the whole column is hidden on the Approved and Rejected sub-tabs —
      // kept on "All" since that still contains pending rows alongside
      // approved ones.
      ...(permissions?.canMoveToScriptReview || permissions?.canApproveOnBehalfOfBrand
        ? creatorsSubTab !== 'approved' && creatorsSubTab !== 'rejected'
          ? [
              {
                id: 'quick',
                alwaysVisible: true,
                header: 'Quick Actions',
                // Two pill buttons ("As Brand" + "Add Script") side by side.
                minWidth: 260,
                maxWidth: 280,
                align: 'center',
                cell: (c: CreatorsRow) => renderRowActions(c, 'quick'),
              } as ColumnDef<CreatorsRow>,
            ]
          : []
        : []),
      // Manage — gated on the underlying capabilities, not a role tier: a
      // Staff (admin_member) viewer with a write+ level campaign grant sees
      // this column too, since the BE allows them to act at that level. The
      // row-management actions (Edit, Remove, Reinstate, Roll back)
      // live in their own column so the everyday Actions cluster stays a
      // clean Approve · Chat · Reject.
      ...(permissions?.canRemoveOnPending ||
      permissions?.canReinstateRejected ||
      (isAdminViewer && creatorsSubTab === 'approved' && openRollbackToPending)
        ? [
            {
              id: 'manage',
              alwaysVisible: true,
              header: 'Manage',
              // A pending row shows up to 2 icon buttons at once (Edit +
              // Remove); Reinstate only ever appears on rejected rows,
              // alongside nothing else. Approve as Brand lives in Quick
              // Actions, so no pill button needs to fit here.
              minWidth: 100,
              maxWidth: 120,
              align: 'center',
              cell: (c: CreatorsRow) => renderRowActions(c, 'manage'),
            } as ColumnDef<CreatorsRow>,
          ]
        : []),
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps -- capability callbacks from the zustand store (approve/reject/remove/edit/recheck) and the modal setters are referentially stable; only the reactive inputs the column cells read are listed, so a mutation's pending flip doesn't rebuild every column.
  }, [
    campaignPlatform,
    campaignId,
    fetchesMetrics,
    metricsCopy,
    hasProfileRecheckAction,
    lane,
    recheckProfile,
    hasMetricsRecheckAction,
    recheckMetrics,
    now,
    creatorsSubTab,
    isAdminViewer,
    optimisticDeliverableMap,
    commercialPopover,
    bulkActions.needsCommercialIds,
    bulkActions.unofferableIds,
    permissions,
    isPublicView,
    handlePublicViewInsights,
    handlePublicRosterAction,
    openRollbackToPending,
    rollbackToPendingBlockedReason,
  ]);

  // Server-persisted column visibility for the roster table (Columns
  // dropdown) — holds per user across devices/browsers; see
  // useColumnPreferences.
  const columnPrefs = useRosterColumnPreferences(campaignId, 'creators');
  const columnVisibility = React.useMemo(
    () => ({
      ...columnVisibilityView(columns, columnPrefs.hiddenIds),
      toggle: columnPrefs.toggle,
      reset: columnPrefs.reset,
    }),
    [columns, columnPrefs.hiddenIds, columnPrefs.toggle, columnPrefs.reset],
  );

  const handlePublicPillClick = usePublicPillCapture('creators', creatorsSubTab);

  // When a resumed save_deliverables selection is active for the
  // currently-open popover's creator, feed it into the rows CommercialPopover
  // reads its initial checkbox state from. A no-op (identical reference via
  // the `!override` branch) for every normal, non-resumed open, so it cannot
  // affect the in-popover editing flow's own onOptimisticChange updates.
  //
  // Must stay above the `!permissions` guard below (rules of hooks).
  const commercialPopoverRows = React.useMemo(() => {
    if (!commercialPopover) return visibleCreators;
    const override = optimisticDeliverableMap.get(commercialPopover.creatorId);
    if (!override) return visibleCreators;
    return visibleCreators.map((r) =>
      r.id === commercialPopover.creatorId
        ? { ...r, deliverables: r.deliverables.map((d) => ({ ...d, is_selected: override.includes(d.type) })) }
        : r,
    );
  }, [visibleCreators, commercialPopover, optimisticDeliverableMap]);

  // All hooks called above. Guard after hook calls so hook order is stable
  // across renders regardless of whether capabilities have loaded yet.
  if (!permissions) return null;

  // ── Confirm-action callbacks wired to ApprovalCard ────────────────────────
  const handleConfirmClose = () => {
    if (confirmBusy) return;
    setConfirmAction(null);
    setApproveAsBrandDeliverables([]);
  };

  // Does `variant` move the row OUT of the currently-active sub-tab's status
  // filter? (Determines whether to optimistically hide it.)
  //   pending  : approve → leaves (pending→accepted_by_brand); reject/remove → leaves
  //   approved : admin-approve KEEPS it (→accepted_by_admin still lives here); reject/remove → leaves
  //   all      : approve keeps it (all = pending+approved+admin); reject/remove → leaves
  const removesRowFromCurrentSubTab = (variant: ConfirmAction['variant']): boolean => {
    if (variant === 'reject' || variant === 'delete') return true;
    if (variant === 'reinstate') return true; // leaves the Rejected sub-tab
    if (variant === 'brand-approve' || variant === 'approve-as-brand') {
      return creatorsSubTab === 'pending';
    }
    // admin-approve: accepted_by_brand → accepted_by_admin, both of which live on
    // the "approved" / "all" sub-tabs, so the row stays (status changes only).
    return false;
  };

  const handleConfirm = async (reason?: string, typedConfirm?: { confirm: string }) => {
    if (!confirmAction) return;
    const { variant, creator } = confirmAction;
    setConfirmBusy(true);
    let didTransition = false;
    try {
      if (variant === 'approve-as-brand') {
        // Volatile selection — sent atomically with approve, never
        // persisted as a draft brand could see. Full array (main + addons).
        if (approveCreatorAsBrand && approveAsBrandDeliverables.length > 0) {
          await approveCreatorAsBrand(creator as unknown as RosterRowRef, approveAsBrandDeliverables);
          didTransition = true;
        }
      } else if (variant === 'brand-approve' || variant === 'admin-approve') {
        if (confirmCreatorApproval && approveAsBrandDeliverables.length > 0) {
          await confirmCreatorApproval(creator as unknown as Record<string, unknown>, approveAsBrandDeliverables);
          didTransition = true;
        }
      } else if (variant === 'reject') {
        if (rejectCreatorApproval) {
          await rejectCreatorApproval(creator as unknown as RosterRowRef, reason ?? '');
          didTransition = true;
        }
        setCreatorDecisions?.((prev: Record<string, CreatorDecision>) => ({
          ...prev,
          [String(creator.id)]: 'rejected' as CreatorDecision,
        }));
      } else if (variant === 'delete') {
        // typedConfirm is always present for 'delete' — ActionConfirmDialog
        // only arms its confirm button once the fixed word matches — but the
        // BE requires the value, so never call without it.
        if (deleteCreatorFromCampaign && typedConfirm) {
          await deleteCreatorFromCampaign(creator as unknown as RosterRowRef, typedConfirm.confirm);
          didTransition = true;
        }
      } else if (variant === 'reinstate') {
        if (reinstateCreator) {
          await reinstateCreator(creator as unknown as RosterRowRef);
          didTransition = true;
        }
      }
      // Client-level filter: hide the row from the current sub-tab the instant
      // the transition succeeds. The `rosterAll` refetch (BE-level filter) is
      // the source of truth and clears the id once the row is gone — fired
      // from onSuccess for remove/reinstate, and from the SSE handler for the
      // approve/reject variants (their mutations skip invalidate() so they
      // don't race the BE's async chain). That prefix also covers the
      // /roster/counts key, so the pill badge refreshes in the same round.
      if (didTransition && removesRowFromCurrentSubTab(variant)) {
        const removedId = String(creator.id);
        setOptimisticallyRemovedIds((prev) => {
          if (prev.has(removedId)) return prev;
          const next = new Set(prev);
          next.add(removedId);
          return next;
        });
      }
      setConfirmAction(null);
      setApproveAsBrandDeliverables([]);
    } catch {
      // Mutation surfaces toast; keep dialog open so user can retry.
    } finally {
      setConfirmBusy(false);
    }
  };

  // ── Public-mode pagination display (grant snapshot, not the disabled
  // hook's own — see useRosterStatusTabs's buildPageQuery comment above).
  // The page-number controls (1 2 3 …) are always real — `displayTotalPages`
  // comes straight off the grant/roster-page snapshot, never fabricated.
  //
  // The footer's "Showing X–Y of Z {noun}" TEXT is a different matter: there
  // is no campaign-wide "total creators" in the public payload, only the one
  // already-fetched page's row count. For a single-page grant
  // (`displayTotalPages <= 1`) that row count genuinely IS the whole total —
  // real, not fabricated. For a MULTI-page grant it is NOT — a prior version
  // of this code reconstructed a total by assuming every other page was
  // full-sized, which is a confidently-wrong number with more authority than
  // an honest "unknown". `hideSummary` (passed to RosterTableShell below)
  // suppresses that text specifically whenever `displayTotalPages > 1`,
  // while the real page-number buttons stay visible or (in real react-query
  // terms) inert-but-visible per the shell's click-interceptor. Same
  // `displayTotalPages > 1` condition drives the granted pill's badge
  // (displayPills above) for the identical reason — the two must never
  // disagree about whether the total is known.
  const publicPageSize = grant?.pageSize ?? pageSize;
  const publicGrantedRowCount = rosterPage?.rows.length ?? 0;
  const displayPage = isPublicView ? (rosterPage?.page ?? grant?.page ?? 1) : page;
  const displayTotalPages = isPublicView
    ? (rosterPage?.total_pages ?? grant?.totalPages ?? 1)
    : pageCount;
  const hideSummary = isPublicView && displayTotalPages > 1;
  // When the real total is unknown (hideSummary), this value is NEVER
  // rendered — its only job is to stay non-zero so the pager's own
  // `total === 0` "nothing to page" guard doesn't hide the (real,
  // known-count) page-number buttons. `Math.max(..., 1)` is safe here
  // specifically because `displayTotalPages > 1` already proves more than
  // one page of real data exists.
  const displayTotal = isPublicView
    ? displayTotalPages > 1
      ? Math.max(publicGrantedRowCount, 1)
      : publicGrantedRowCount
    : total;

  const approveControl = pickApproveControl(
    selectionApproveControl(bulkActions),
    approveAllControl(showApproveAll, approveAllEligibleCount, approveAllMutation.isPending, () => void handleApproveAll()),
  );
  const adminApproveControl = isAdminViewer ? approveControl : null;
  const brandApproveControl = isAdminViewer ? null : approveControl;
  const showDeleteSelected = bulkActions.canDeleteSelected && bulkActions.selectedCount > 0;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col gap-5">
      <RosterScreenHeader title="Creators" subtitle="The people who could fit best." />

      <RosterKpiCard
        reach={kpi?.estimated_reach ?? 0}
        budget={kpi?.estimated_budget ?? 0}
        creatorCount={kpi?.creator_count ?? 0}
        creatorCountLabel="Evaluating"
        // A DISABLED react-query hook reports isLoading: false (react-query
        // v5: isLoading = isPending && isFetching, and a never-fetched
        // disabled query has isFetching: false) — so kpiQuery.isLoading
        // alone would render 0/₹0 as if that were the real answer. Forcing
        // it true in public mode is not optional here.
        loading={isPublicView || kpiQuery.isLoading}
      />

      <JumpToTopPill
        isRefetching={isLoadingEntries}
        page={page}
        onJump={() => setPage(1)}
      />

      <RosterTableShell<CreatorsSubTab>
        fill
        activeTab={creatorsSubTab}
        // Public mode prompts sign-in (with the click captured for resume)
        // instead of switching pills directly — see handlePublicPillClick.
        onTabChange={isPublicView ? handlePublicPillClick : setCreatorsSubTab}
        // Counts come from /roster/counts (stable across pagination), so the
        // pill row must not flash a skeleton when a page fetch is in flight.
        // Public mode never shows the whole-row skeleton — displayPills
        // already carries a per-pill 'loading' sentinel for every pill
        // except the granted one (see PhaseSubTabs).
        tabsLoading={isPublicView ? false : !rosterCounts}
        tabs={displayPills}
        page={displayPage}
        totalPages={displayTotalPages}
        total={displayTotal}
        // Never a fabricated campaign-wide total — see the displayTotal
        // comment above.
        hideSummary={hideSummary}
        onPage={isPublicView ? PUBLIC_NOOP : setPage}
        pageSize={isPublicView ? publicPageSize : pageSize}
        pageSizeOptions={isPublicView ? undefined : PAGE_SIZE_OPTIONS}
        onPageSizeChange={isPublicView ? undefined : setPageSize}
        controls={
          isPublicView ? undefined : (
          <>
            {(adminApproveControl || showDeleteSelected || permissions.canUploadCreatorSheet) && (
              <div className="flex items-center gap-2">
                {/* Admin: compact icons (space-constrained row). Brand gets the
                    full-text pill below instead. A checkbox selection turns the
                    approve button into "Approve selected". */}
                {adminApproveControl && <ApproveIconButton control={adminApproveControl} />}
                {showDeleteSelected && (
                  <DeleteSelectedIconButton count={bulkActions.selectedCount} onClick={bulkActions.openDelete} />
                )}
                {permissions.canUploadCreatorSheet && effectiveResult && (
                  <SimpleTooltip content="View last import">
                    <Button
                      variant="outline"
                      size="icon"
                      type="button"
                      onClick={() => setImportStatusOpen(true)}
                      aria-label="View last import"
                      className="size-10 shrink-0 rounded-lg border-foreground/30 text-foreground hover:bg-muted"
                    >
                      <Info size={16} />
                    </Button>
                  </SimpleTooltip>
                )}
                {permissions.canUploadCreatorSheet && (
                  <SimpleTooltip content="Upload creator">
                    <Button
                      variant="outline"
                      size="icon"
                      type="button"
                      onClick={() => setUploadModalOpen(true)}
                      aria-label="Upload creator"
                      className="size-10 shrink-0 rounded-lg border-foreground bg-transparent text-foreground transition-colors hover:bg-foreground hover:text-white"
                    >
                      <UploadSimple size={18} />
                    </Button>
                  </SimpleTooltip>
                )}
              </div>
            )}
            {brandApproveControl && <ApprovePillButton control={brandApproveControl} />}
            <CreatorsControls
              query={searchState.query}
              onQueryChange={searchState.setQuery}
              columnsMenu={
                <ColumnsMenu
                  items={columnVisibility.menuItems}
                  hiddenCount={columnVisibility.hiddenCount}
                  onToggle={columnVisibility.toggle}
                  onReset={columnVisibility.reset}
                />
              }
            />
          </>
          )
        }
      >
        {/* DataTable handles loading/empty states and horizontal scroll.
            border-0/rounded-none so the Card shell (RosterTableShell) provides
            the outer border + rounding — same pattern as ConfirmedTab. Error
            is a plain message here (matching Scripts/Content/Live-Content)
            rather than DataTable's error prop — the shared hook surfaces
            isError as a blanket flag across 4 pills, not a single query's
            error object to humanize. */}
        {isCreatorsQueryError ? (
          <p className="py-16 text-center text-sm text-rose-500">
            Couldn't load creators. Please refresh.
          </p>
        ) : (
        <DataTable<CreatorsRow>
          fillHeight
          columns={columnVisibility.visibleColumns}
          data={visibleCreators}
          getRowId={(c) => String(c.id)}
          // Server-side column sort (disabled in read-only public view). A click
          // updates this tab's persisted {by,dir} + resets to page 1.
          sort={rosterSort.sortProp(creatorsSubTab, { disabled: isPublicView, onChange: () => setPage(1) })}
          // Checkbox column + select-all, only on tabs where the viewer has a bulk action.
          rowSelection={bulkActions.rowSelection}
          // isInitialLoading (from the hook's disabled, forever-unsettled
          // query) would stay true forever in public mode — but the real
          // granted rows are ALREADY available synchronously from the public
          // payload (fetched once, up-front, by PublicShareViewPage), so the
          // table must render them immediately rather than sitting in a
          // permanent row-skeleton state.
          loading={isPublicView ? false : isInitialLoading}
          emptyState={
            // Per-filter empties win first — a specific sub-tab (Pending/
            // Approved/Rejected) shows its own message even when the campaign
            // has zero creators. The "Analyzing / upload" global state is
            // reserved for the All view (the true "nothing yet" surface).
            creatorsSubTab === 'pending' ? (
              <EmptyState
                icon={UserCheck}
                title="No creators pending"
                body="You've reviewed every recommendation. New matches land here."
                action={{ label: 'View approved creators', onClick: () => setCreatorsSubTab('approved') }}
              />
            ) : creatorsSubTab === 'approved' ? (
              <EmptyState
                icon={UserCircleCheck}
                title="No approved creators yet"
                body="Approve creators from Pending to build your shortlist."
                action={{ label: 'Review pending creators', onClick: () => setCreatorsSubTab('pending') }}
              />
            ) : creatorsSubTab === 'rejected' ? (
              <EmptyState
                icon={Prohibit}
                title="No rejected creators"
                body={
                  isAdminViewer
                    ? 'Rejected creators land here; send any of them back to Pending anytime. Deleted creators are gone for good and never appear here.'
                    : 'Creators you pass on move here.'
                }
                action={{ label: 'Back to pending', onClick: () => setCreatorsSubTab('pending') }}
              />
            ) : allCount === 0 ? (
              permissions.canUploadCreatorSheet ? (
                <EmptyState
                  icon={UploadSimple}
                  title="No creators uploaded yet"
                  body="Upload a creator sheet above to get started."
                />
              ) : (
                <EmptyState
                  icon={UsersThree}
                  title="Creators are being curated"
                  body="The Discovr team is hand-picking creators for this campaign, the first batch usually lands within a day."
                  action={{
                    label: 'View Brief',
                    onClick: () =>
                      navigate({
                        to: '.',
                        search: ((p: Partial<CampaignSearch>) => ({ ...p, tab: 'details', pill: undefined })) as never,
                      }),
                  }}
                />
              )
            ) : (
              <EmptyState
                icon={UsersThree}
                title="No creators yet"
                body="Creators will appear here."
              />
            )
          }
          className="border-0 rounded-none bg-transparent"
        />
        )}
      </RosterTableShell>

      {/* Staged import-review wizard — opened from the "Upload creator" button
          next to the search. Import progress/results render inline as its own
          steps; ImportStatusModal below is not used for this path. */}
      {permissions.canUploadCreatorSheet && (
        <CreatorImportWizard
          open={uploadModalOpen}
          campaignId={campaignId ?? ''}
          api={importBatchApi}
          campaignName={campaignHeader?.name}
          payoutModel={campaignHeader?.payout_model}
          campaignPlatform={campaignPlatform}
          onRosterStale={refreshRoster}
          onImported={showImportedCreators}
          onClose={closeUploadWizard}
        />
      )}

      {/* Import progress + results, opened only from the "View last import" button. */}
      {permissions.canUploadCreatorSheet && (
        <ImportStatusModal
          open={importStatusOpen}
          onClose={() => setImportStatusOpen(false)}
          progress={importProgress}
          result={effectiveResult}
          onResetResult={() => setImportStatusOpen(false)}
          onUploadAnother={() => {
            setImportStatusOpen(false);
            setUploadModalOpen(true);
          }}
        />
      )}

      {/* Shared confirm dialog for every row action (approve-as-brand /
          brand-approve / admin-approve / reject / delete / reinstate). */}
      {confirmAction && (
        <ApprovalCard
          confirmAction={confirmAction}
          confirmBusy={confirmBusy}
          approveAsBrandDeliverables={approveAsBrandDeliverables}
          campaignPlatform={campaignPlatform}
          isAdminViewer={isAdminViewer}
          onClose={handleConfirmClose}
          onConfirm={handleConfirm}
          onSetApproveAsBrandDeliverables={setApproveAsBrandDeliverables}
          approveCreatorAsBrand={approveCreatorAsBrand}
          rejectCreatorApproval={rejectCreatorApproval}
        />
      )}

      {bulkActions.dialogs}

      {addScriptTarget && addScriptTarget.stage !== 'script' && (
        <UrlInputModal
          open
          onClose={() => setAddScriptTarget(null)}
          onSave={async (url) => {
            const publish = addScriptTarget.stage === 'live' ? approveCreatorAndGoLive : approveCreatorAndAddContent;
            if (!publish) throw new Error('You do not have permission to do this.');
            const result = await publish(
              addScriptTarget.creator as unknown as RosterRowRef,
              addScriptTarget.deliverables,
              url,
            );
            // Nothing was approved when the first stage fails: keep the dialog open.
            if ((result as { stage?: string } | undefined)?.stage === 'approve_as_brand') {
              throw new Error('Approval failed — the creator was not moved.');
            }
            setAddScriptTarget(null);
          }}
          title={addScriptTarget.stage === 'live' ? 'Add live URL' : 'Add content URL'}
          description={
            addScriptTarget.stage === 'live'
              ? liveUrlCopy(campaignPlatform).description
              : uploadCopy(campaignPlatform).description
          }
          placeholder={
            addScriptTarget.stage === 'live'
              ? liveUrlCopy(campaignPlatform).placeholder
              : uploadCopy(campaignPlatform).placeholder
          }
          saveLabel={addScriptTarget.stage === 'live' ? 'Approve & go live' : 'Approve & submit'}
          requirePlatform={
            addScriptTarget.stage === 'live'
              ? liveUrlCopy(campaignPlatform).requirePlatform
              : uploadCopy(campaignPlatform).requirePlatform
          }
          expectedContentKind={deliverableContentKind(
            addScriptTarget.deliverables.find((d) => isMain(d, campaignPlatform ?? '')),
          )}
        />
      )}

      {addScriptTarget && addScriptTarget.stage === 'script' && (
        <AddScriptModal
          open
          onClose={() => {
            if (addScriptLoading) return;
            setAddScriptTarget(null);
          }}
          creatorName={rowDisplayName(addScriptTarget.creator)}
          creatorId={addScriptTarget.creator.id}
          rosterEntryId={addScriptTarget.creator.id}
          isLoading={addScriptLoading}
          onScriptSubmit={async (_creatorId, _rosterEntryId, doc) => {
            if (!moveCreatorToScriptReview) return;
            try {
              setAddScriptLoading(true);
              const result = await moveCreatorToScriptReview(
                addScriptTarget.creator as unknown as RosterRowRef,
                addScriptTarget.deliverables,
                doc,
              );
              const stage = (result as { stage?: string } | undefined)?.stage;
              // approve_as_brand failing means NOTHING happened server-side
              // (entry is still pending) — throw so AddScriptModal's own
              // catch keeps the dialog open with the typed script intact,
              // instead of silently closing as if it had succeeded. Every
              // other outcome (no stage, or a later stage) means the entry
              // already left pending — this modal's job here is done, even
              // when a later step needs resuming elsewhere (the mutation's
              // own toast already says where).
              if (stage === 'approve_as_brand') {
                throw new Error('Approval failed — the creator was not moved.');
              }
              setAddScriptTarget(null);
            } finally {
              setAddScriptLoading(false);
            }
          }}
        />
      )}

      {/* ── Commercial Popover ── fixed-position, selectable list from backend data ─── */}
      {commercialPopover && (
        <CommercialPopover
          key={commercialPopover.creatorId}
          popover={commercialPopover}
          rows={commercialPopoverRows}
          popoverRef={commercialPopoverRef}
          platform={campaignPlatform ?? ''}
          isPublicView={isPublicView}
          campCode={publicCampCode}
          tail={publicTail}
          requestLogin={requestLogin}
          selectCreatorDeliverable={selectCreatorDeliverable}
          onOptimisticChange={(creatorId, selectedTypes) => {
            setOptimisticDeliverableMap((prev) => {
              const next = new Map(prev);
              next.set(creatorId, selectedTypes);
              return next;
            });
          }}
          onClose={() => setCommercialPopover(null)}
        />
      )}
      {rollbackDialogs}
    </div>
  );
}
